import { Fragment, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { WithContext as ReactTags } from "react-tag-input";
import { XMarkIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import { privateAxios } from "../utils/axios";

const KeyCodes = {
  comma: 188,
  enter: 13,
  backspace: 8,
};
const delimiters = [KeyCodes.comma, KeyCodes.enter];

const emptyReturnData = {
  sourceExpense: "",
  sourceInvoiceNumber: "",
  receiverName: "",
  receiverPhone: "",
  receiverAddress: "",
  driverName: "",
  driverPhone: "",
  driverAddress: "",
  transportNumber: "",
  ref: "",
  note: "",
  reason: "",
  returnDate: new Date().toISOString().split("T")[0],
  products: [],
  totalAmount: 0,
  subTotal: 0,
};

export default function AddReturnDetails({
  addReturnModalSetting,
  handlePageUpdate,
}) {
  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const cancelButtonRef = useRef(null);

  const [invoiceInput, setInvoiceInput] = useState("");
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState("");

  const [returnData, setReturnData] = useState(emptyReturnData);

  const calculateTotals = (products) => {
    const subTotal = products.reduce((sum, item) => {
      return sum + item.quantity * item.price;
    }, 0);
    return { subTotal, totalAmount: subTotal };
  };

  const handleFindInvoice = async () => {
    if (!invoiceInput.trim()) {
      setLookupError("Enter an invoice number");
      return;
    }

    setLookupLoading(true);
    setLookupError("");
    try {
      const res = await privateAxios.get("/returns/invoice-lookup", {
        params: { invoiceNumber: invoiceInput.trim() },
      });

      if (!res.data.success || !res.data.found || !res.data.data) {
        setLookupError("No expense found for this invoice number");
        setReturnData(emptyReturnData);
        return;
      }

      const expense = res.data.data;

      const products = (expense.products || []).map((item) => ({
        _rowId: item.product._id,
        product: item.product._id,
        productName: item.product.name,
        productCategory: item.product.category,
        productUnit: item.product.unit,
        price: item.price,
        skus: [],
        quantity: 0,
        warrantyExpireDate: item.warrantyExpireDate || "",
      }));

      const totals = calculateTotals(products);

      setReturnData({
        sourceExpense: expense._id,
        sourceInvoiceNumber: expense.invoiceNumber,
        receiverName: expense.receiverName,
        receiverPhone: expense.receiverPhone,
        receiverAddress: expense.receiverAddress,
        driverName: expense.driverName,
        driverPhone: expense.driverPhone,
        driverAddress: expense.driverAddress,
        transportNumber: expense.transportNumber,
        ref: expense.ref || "",
        note: "",
        reason: "",
        returnDate: new Date().toISOString().split("T")[0],
        products,
        ...totals,
      });
      toast.success(`Invoice ${expense.invoiceNumber} loaded`);
    } catch (err) {
      console.error("Error looking up invoice:", err);
      setLookupError(
        err?.response?.data?.message || "Failed to look up invoice"
      );
      setReturnData(emptyReturnData);
    } finally {
      setLookupLoading(false);
    }
  };

  const handleSkusChange = (index, newSkus) => {
    setReturnData((prev) => {
      const updatedProducts = [...prev.products];
      updatedProducts[index] = {
        ...updatedProducts[index],
        skus: newSkus,
        quantity: newSkus.length,
      };
      const totals = calculateTotals(updatedProducts);
      return { ...prev, products: updatedProducts, ...totals };
    });
  };

  const checkSkuEligible = async (productId, sku) => {
    try {
      const res = await privateAxios.get("/returns/stock-check-by-sku", {
        params: {
          productId,
          sku,
          sourceExpense: returnData.sourceExpense,
        },
      });

      return res.data;
    } catch (err) {
      return { eligible: false };
    }
  };

  const validateFields = () => {
    const newErrors = {};

    if (!returnData.sourceExpense) {
      newErrors.invoice = "Find a valid invoice first";
    }
    if (!returnData.reason?.trim()) {
      newErrors.reason = "Reason is required";
    }
    if (!returnData.returnDate) {
      newErrors.returnDate = "Date is required";
    }

    const hasAnySku = returnData.products.some((p) => p.skus.length > 0);
    if (!hasAnySku) {
      newErrors.products = "Add at least one SKU to return";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateFields()) {
      toast.error("Please fix the errors before submitting");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...returnData,
        products: returnData.products
          .filter((p) => p.skus.length > 0)
          .map(({ _rowId, productName, productCategory, productUnit, ...rest }) => rest),
      };
      const response = await privateAxios.post("/returns/", payload);
      if (response.data.success) {
        toast.success("Return added successfully!");
        handlePageUpdate();
        addReturnModalSetting();
      } else {
        toast.error(response.data.message || "Failed to add return");
      }
    } catch (err) {
      console.error("Error adding return:", err);
      toast.error(err?.response?.data?.message || "Failed to add return. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const renderError = (fieldName) => {
    if (errors[fieldName]) {
      return <p className="mt-1 text-sm text-red-600">{errors[fieldName]}</p>;
    }
    return null;
  };

  const invoiceLoaded = !!returnData.sourceExpense;

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-10"
        initialFocus={cancelButtonRef}
        onClose={setOpen}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-60 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-5xl h-[90vh] transform overflow-hidden rounded-2xl bg-white p-8 shadow-2xl transition-all flex flex-col">

                <button
                  type="button"
                  className="absolute right-4 top-4 text-gray-400 hover:text-gray-500 focus:outline-none"
                  onClick={() => addReturnModalSetting()}
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                </button>

                <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                  <div className="w-full">
                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                      <Dialog.Title
                        as="h3"
                        className="text-xl font-semibold leading-6 text-gray-900"
                      >
                        Return Entry Form
                      </Dialog.Title>

                      <div className="overflow-auto pr-2 h-[80vh]">
                        <form
                          className="space-y-5 mt-4"
                          onSubmit={(e) => {
                            e.preventDefault();
                            handleSubmit();
                          }}
                        >
                          {/* Invoice lookup */}
                          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Expense Invoice Number *
                            </label>
                            <div className="flex gap-2">
                              <input
                                type="text"
                                value={invoiceInput}
                                onChange={(e) => setInvoiceInput(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    handleFindInvoice();
                                  }
                                }}
                                placeholder="e.g. INV-2026-07-20-001"
                                className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                              />
                              <button
                                type="button"
                                onClick={handleFindInvoice}
                                disabled={lookupLoading}
                                className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
                              >
                                <MagnifyingGlassIcon className="h-4 w-4" />
                                {lookupLoading ? "Searching..." : "Find"}
                              </button>
                            </div>
                            {lookupError && (
                              <p className="mt-1 text-sm text-red-600">{lookupError}</p>
                            )}
                            {renderError("invoice")}
                            {invoiceLoaded && (
                              <p className="mt-2 text-sm text-emerald-700 font-medium">
                                Loaded invoice {returnData.sourceInvoiceNumber}
                              </p>
                            )}
                          </div>

                          {/* Auto-filled, read-only header info */}
                          {invoiceLoaded && (
                            <>
                              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                                <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">
                                  Invoice Information (auto-filled, read-only)
                                </h4>
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                  <div>
                                    <span className="text-gray-500">Receiver Name</span>
                                    <p className="font-medium text-gray-900">{returnData.receiverName}</p>
                                  </div>
                                  <div>
                                    <span className="text-gray-500">Receiver Phone</span>
                                    <p className="font-medium text-gray-900">{returnData.receiverPhone}</p>
                                  </div>
                                  <div>
                                    <span className="text-gray-500">Receiver Address</span>
                                    <p className="font-medium text-gray-900">{returnData.receiverAddress || "-"}</p>
                                  </div>
                                  <div>
                                    <span className="text-gray-500">Driver Name</span>
                                    <p className="font-medium text-gray-900">{returnData.driverName}</p>
                                  </div>
                                  <div>
                                    <span className="text-gray-500">Driver Phone</span>
                                    <p className="font-medium text-gray-900">{returnData.driverPhone}</p>
                                  </div>
                                  <div>
                                    <span className="text-gray-500">Driver Address</span>
                                    <p className="font-medium text-gray-900">{returnData.driverAddress || "-"}</p>
                                  </div>
                                  <div>
                                    <span className="text-gray-500">Transport Number</span>
                                    <p className="font-medium text-gray-900">{returnData.transportNumber}</p>
                                  </div>
                                  <div>
                                    <span className="text-gray-500">Ref No</span>
                                    <p className="font-medium text-gray-900">{returnData.ref || "-"}</p>
                                  </div>
                                </div>
                              </div>

                              {/* Editable return-specific fields */}
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700">
                                    Reason *
                                  </label>
                                  <input
                                    type="text"
                                    value={returnData.reason}
                                    onChange={(e) =>
                                      setReturnData((prev) => ({ ...prev, reason: e.target.value }))
                                    }
                                    placeholder="e.g. damaged, wrong item, warranty claim"
                                    className={`mt-1 block w-full rounded-md border ${
                                      errors.reason ? "border-red-300" : "border-gray-300"
                                    } px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500`}
                                  />
                                  {renderError("reason")}
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700">
                                    Return Date *
                                  </label>
                                  <input
                                    type="date"
                                    value={returnData.returnDate}
                                    onChange={(e) =>
                                      setReturnData((prev) => ({ ...prev, returnDate: e.target.value }))
                                    }
                                    className={`mt-1 block w-full rounded-md border ${
                                      errors.returnDate ? "border-red-300" : "border-gray-300"
                                    } px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500`}
                                  />
                                  {renderError("returnDate")}
                                </div>
                              </div>

                              {/* Products from invoice, SKU entry only */}
                              <div className="space-y-3">
                                <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-gray-600 bg-gray-50 p-2 rounded-xl">
                                  <div className="col-span-6">Product</div>
                                  <div className="col-span-3">SKUs to Return</div>
                                  <div className="col-span-1">Qty</div>
                                  <div className="col-span-1">Price</div>
                                  <div className="col-span-1">Total</div>
                                </div>

                                {returnData.products.map((item, i) => (
                                  <div
                                    key={item._rowId}
                                    className="grid grid-cols-12 gap-2 p-3 border rounded-xl items-start"
                                  >
                                    <div className="col-span-6">
                                      <p className="font-semibold text-gray-900 text-sm">{item.productName}</p>
                                      <p className="text-xs text-gray-500">
                                        {item.productCategory} · {item.productUnit}
                                      </p>
                                    </div>

                                    <div className="col-span-3">
                                      <div className="rounded-md border border-dashed border-gray-300 px-2 py-1 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 transition">
                                        <ReactTags
                                          tags={(item.skus || []).map((s) => ({ id: s, text: s }))}
                                          delimiters={delimiters}
                                          handleAddition={async (tag) => {
                                            try {
                                              const { eligible, message } = await checkSkuEligible(
                                                item.product,
                                                tag.text
                                              );

                                              if (!eligible) {
                                                toast.error(message || "SKU is not eligible for return");
                                                return;
                                              }

                                              const newSkus = [...(item.skus || []), tag.text];
                                              handleSkusChange(i, newSkus);
                                              toast.success("SKU added");
                                            } catch (err) {
                                              console.error(err);
                                              toast.error("Unable to validate SKU");
                                            }
                                          }}
                                          handleDelete={(idx) => {
                                            const removedSku = item.skus?.[idx];
                                            const updatedSkus = (item.skus || []).filter((_, x) => x !== idx);
                                            handleSkusChange(i, updatedSkus);
                                            if (removedSku) {
                                              toast.success(`SKU "${removedSku}" removed`);
                                            }
                                          }}
                                          placeholder="Scan or type SKU and press Enter"
                                          inputFieldPosition="bottom"
                                          autocomplete
                                          allowUnique
                                        />
                                      </div>
                                    </div>

                                    <input
                                      type="number"
                                      value={item.skus.length}
                                      readOnly
                                      className="col-span-1 h-10 border rounded px-2 bg-gray-50"
                                    />
                                    <input
                                      type="number"
                                      value={item.price}
                                      readOnly
                                      className="col-span-1 h-10 border rounded px-2 bg-gray-50"
                                    />
                                    <input
                                      readOnly
                                      value={item.quantity * item.price}
                                      className="col-span-1 h-10 border rounded px-2 bg-gray-100"
                                    />
                                  </div>
                                ))}

                                {renderError("products")}
                              </div>

                              <div className="mt-4">
                                <label className="block text-sm font-medium text-gray-700">
                                  Note
                                </label>
                                <input
                                  type="text"
                                  value={returnData.note}
                                  onChange={(e) =>
                                    setReturnData((prev) => ({ ...prev, note: e.target.value }))
                                  }
                                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                />
                              </div>

                              <div className="flex justify-end gap-4">
                                <div className="w-1/3">
                                  <label className="block text-sm font-medium text-gray-700">
                                    Sub Total
                                  </label>
                                  <input
                                    type="text"
                                    value={returnData.subTotal}
                                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-gray-50 text-right font-medium"
                                    readOnly
                                  />
                                </div>
                                <div className="w-1/3">
                                  <label className="block text-sm font-medium text-gray-700">
                                    Total Amount
                                  </label>
                                  <input
                                    type="text"
                                    value={returnData.totalAmount}
                                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-gray-50 text-right font-medium"
                                    readOnly
                                  />
                                </div>
                              </div>
                            </>
                          )}

                          <div className="mt-6 flex justify-end gap-3">
                            <button
                              type="button"
                              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                              onClick={() => addReturnModalSetting()}
                            >
                              Cancel
                            </button>
                            <button
                              type="submit"
                              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-60"
                              disabled={loading || !invoiceLoaded}
                            >
                              {loading ? "Processing..." : "Save Return"}
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
